"use client";

import { c as _c } from "react/compiler-runtime";
import React from 'react';
import { motion } from 'framer-motion';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/Card';
import { useScrollAnimation } from '@/hooks/useScrollAnimation';
import { BookOpen, Trophy, Briefcase, Video, Users, Award, TrendingUp, Shield, Zap } from 'lucide-react';
export default function Features() {
  const $ = _c(32);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = {
      threshold: 0.2
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const {
    ref,
    isVisible
  } = useScrollAnimation(t0);
  let t1;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = [{
      icon: BookOpen,
      title: "Premium Courses",
      description: "Access 1000+ courses from beginner to advanced. Learn at your own pace with lifetime access.",
      gradient: "from-blue-500 to-cyan-500"
    }, {
      icon: Video,
      title: "Live Interactive Sessions",
      description: "Join exclusive live classes with expert instructors. Ask questions in real-time.",
      gradient: "from-purple-500 to-pink-500"
    }, {
      icon: Trophy,
      title: "Competitive Learning",
      description: "Climb leaderboards, earn ranks, and compete with peers to showcase your expertise.",
      gradient: "from-orange-500 to-red-500"
    }, {
      icon: Briefcase,
      title: "Auto-Match to Jobs",
      description: "Get automatically matched with internships and jobs based on your verified skills.",
      gradient: "from-green-500 to-emerald-500"
    }, {
      icon: Award,
      title: "Verified Certificates",
      description: "Earn industry-recognized certificates that automatically appear in your profile.",
      gradient: "from-indigo-500 to-blue-500"
    }, {
      icon: Users,
      title: "Company Partnerships",
      description: "Direct access to 500+ hiring companies actively seeking talented students.",
      gradient: "from-pink-500 to-rose-500"
    }, {
      icon: TrendingUp,
      title: "Skill Tracking",
      description: "Monitor your progress with detailed analytics and skill assessments.",
      gradient: "from-yellow-500 to-orange-500"
    }, {
      icon: Shield,
      title: "Quality Assurance",
      description: "Companies can report performance issues, ensuring only the best get matched.",
      gradient: "from-teal-500 to-cyan-500"
    }, {
      icon: Zap,
      title: "Fast & Free Options",
      description: "Start learning immediately with our free courses, upgrade anytime for premium content.",
      gradient: "from-violet-500 to-purple-500"
    }];
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const features = t1;
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = {
      opacity: 0
    };
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = {
      hidden: t2,
      visible: {
        opacity: 1,
        transition: {
          staggerChildren: 0.1
        }
      }
    };
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  const containerVariants = t3;
  let t4;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = {
      opacity: 0,
      y: 20
    };
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  let t5;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = {
      hidden: t4,
      visible: {
        opacity: 1,
        y: 0,
        transition: {
          duration: 0.5
        }
      }
    };
    $[5] = t5;
  } else {
    t5 = $[5];
  }
  const itemVariants = t5;
  const t6 = ref as React.RefObject<HTMLElement>;
  let t7;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = {
      opacity: 0,
      y: 20
    };
    $[6] = t7;
  } else {
    t7 = $[6];
  }
  let t8;
  if ($[7] !== isVisible) {
    t8 = isVisible ? {
      opacity: 1,
      y: 0
    } : {};
    $[7] = isVisible;
    $[8] = t8;
  } else {
    t8 = $[8];
  }
  let t9;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = {
      duration: 0.6
    };
    $[9] = t9;
  } else {
    t9 = $[9];
  }
  let t10;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = <div className="inline-flex items-center gap-2 bg-blue-100 text-blue-700 px-4 py-2 rounded-full text-sm font-semibold mb-4"><Zap className="w-4 h-4" /><span>All-in-One Platform</span></div>;
    $[10] = t10;
  } else {
    t10 = $[10];
  }
  let t11;
  let t12;
  if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">Everything You Need to<span className="block bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent">Launch Your Career</span></h2>;
    t12 = <p className="text-xl text-gray-600 max-w-3xl mx-auto">Combining the best of Coursera, LinkedIn, and Upwork into one powerful platform</p>;
    $[11] = t11;
    $[12] = t12;
  } else {
    t11 = $[11];
    t12 = $[12];
  }
  let t13;
  if ($[13] !== t8) {
    t13 = <motion.div initial={t7} animate={t8} transition={t9} className="text-center mb-16">{t10}{t11}{t12}</motion.div>;
    $[13] = t8;
    $[14] = t13;
  } else {
    t13 = $[14];
  }
  const t14 = isVisible ? "visible" : "hidden";
  let t15;
  if ($[15] === Symbol.for("react.memo_cache_sentinel")) {
    t15 = features.map((feature, index) => <motion.div key={index} variants={itemVariants}><Card hover={true} className="h-full"><CardHeader><div className={`w-14 h-14 rounded-xl bg-gradient-to-br ${feature.gradient} flex items-center justify-center mb-4 shadow-lg`}><feature.icon className="w-7 h-7 text-white" /></div><CardTitle>{feature.title}</CardTitle></CardHeader><CardContent><CardDescription>{feature.description}</CardDescription></CardContent></Card></motion.div>);
    $[15] = t15;
  } else {
    t15 = $[15];
  }
  let t16;
  if ($[16] !== t14) {
    t16 = <motion.div variants={containerVariants} initial="hidden" animate={t14} className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">{t15}</motion.div>;
    $[16] = t14;
    $[17] = t16;
  } else {
    t16 = $[17];
  }
  let t17;
  if ($[18] === Symbol.for("react.memo_cache_sentinel")) {
    t17 = {
      opacity: 0,
      y: 20
    };
    $[18] = t17;
  } else {
    t17 = $[18];
  }
  let t18;
  if ($[19] !== isVisible) {
    t18 = isVisible ? {
      opacity: 1,
      y: 0
    } : {};
    $[19] = isVisible;
    $[20] = t18;
  } else {
    t18 = $[20];
  }
  let t19;
  if ($[21] === Symbol.for("react.memo_cache_sentinel")) {
    t19 = {
      duration: 0.6,
      delay: 0.8
    };
    $[21] = t19;
  } else {
    t19 = $[21];
  }
  let t20;
  if ($[22] === Symbol.for("react.memo_cache_sentinel")) {
    t20 = <div className="inline-flex flex-col sm:flex-row items-center gap-4 bg-gradient-to-r from-blue-50 to-indigo-50 px-8 py-6 rounded-2xl border border-blue-200"><div className="text-left"><h3 className="text-lg font-bold text-gray-900">Ready to transform your future?</h3><p className="text-gray-600">Join thousands of successful students today</p></div><button className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white px-6 py-3 rounded-lg font-semibold hover:from-blue-700 hover:to-indigo-700 transition-all shadow-lg hover:shadow-xl whitespace-nowrap">Get Started Free</button></div>;
    $[22] = t20;
  } else {
    t20 = $[22];
  }
  let t21;
  if ($[23] !== t18) {
    t21 = <motion.div initial={t17} animate={t18} transition={t19} className="mt-16 text-center">{t20}</motion.div>;
    $[23] = t18;
    $[24] = t21;
  } else {
    t21 = $[24];
  }
  let t22;
  if ($[25] !== t13 || $[26] !== t16 || $[27] !== t21) {
    t22 = <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">{t13}{t16}{t21}</div>;
    $[25] = t13;
    $[26] = t16;
    $[27] = t21;
    $[28] = t22;
  } else {
    t22 = $[28];
  }
  let t23;
  if ($[29] !== t22 || $[30] !== t6) {
    t23 = <section id="features" className="py-20 md:py-32 bg-white" ref={t6}>{t22}</section>;
    $[29] = t22;
    $[30] = t6;
    $[31] = t23;
  } else {
    t23 = $[31];
  }
  return t23;
}